import { useState, useCallback, useRef } from 'react';
import * as api from '../services/api-client';

/**
 * Resolved user data returned from the directory service.
 */
export interface ResolvedUser {
  readonly displayName: string;
  readonly mail: string | null;
}

/**
 * Return type for the useUserResolve hook.
 */
export interface UseUserResolveReturn {
  readonly resolvedUsers: Record<string, ResolvedUser>;
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly resolve: (userIds: string[]) => void;
}

interface ResolveUsersResponse {
  readonly users: Record<string, ResolvedUser>;
}

/**
 * Hook for batch-resolving user IDs to display names and emails via the
 * user-directory backend. Accumulates resolved users across multiple calls
 * and skips IDs that have already been resolved.
 *
 * Errors are captured in state without throwing, so page rendering continues
 * with fallback data (email or userId) when the directory service is unavailable.
 *
 * Validates: Requirements 5.1, 5.2, 5.3, 5.4
 */
export function useUserResolve(): UseUserResolveReturn {
  const [resolvedUsers, setResolvedUsers] = useState<Record<string, ResolvedUser>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use a ref to access current resolvedUsers inside the callback without
  // needing it as a dependency (avoids stale closure issues).
  const resolvedUsersRef = useRef(resolvedUsers);
  resolvedUsersRef.current = resolvedUsers;

  const resolve = useCallback((userIds: string[]) => {
    // Filter out IDs already resolved
    const remainingIds = userIds.filter((id) => !(id in resolvedUsersRef.current));

    if (remainingIds.length === 0) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const MAX_RETRIES = 2;
    const RETRY_DELAYS = [1500, 3000];

    const attemptResolve = async (attempt: number): Promise<void> => {
      try {
        const response = await api.post<ResolveUsersResponse>('/admin/users/resolve', { userIds: remainingIds });
        setResolvedUsers((prev) => ({
          ...prev,
          ...response.users,
        }));
        setIsLoading(false);
      } catch (err: unknown) {
        const isRetryable =
          err instanceof api.NetworkError ||
          (err instanceof api.ApiErrorResponse && err.status >= 500);

        if (isRetryable && attempt < MAX_RETRIES) {
          await new Promise((r) => setTimeout(r, RETRY_DELAYS[attempt]));
          return attemptResolve(attempt + 1);
        }

        if (err instanceof api.ApiErrorResponse) {
          setError(err.payload.message);
        } else if (err instanceof api.NetworkError) {
          setError('Network error. Unable to resolve user details.');
        } else {
          setError('An unexpected error occurred while resolving user details.');
        }
        setIsLoading(false);
      }
    };

    attemptResolve(0);
  }, []);

  return { resolvedUsers, isLoading, error, resolve };
}
